import React, { useEffect, useMemo, useRef, useState } from "react";
import { LightboxModal } from "../../homepage/components/LightboxModal";

function filenameToLabel(filename) {
  return filename
    .replace(/\.[^.]+$/, "")
    .replace(/[_-]+/g, " ")
    .trim();
}

export function BookCoverCarousel({ items }) {
  const covers = useMemo(() => (items ?? []).filter(Boolean), [items]);
  const [index, setIndex] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const thumbsRef = useRef(null);

  const hasCovers = covers.length > 0;
  const active = hasCovers ? covers[Math.max(0, Math.min(index, covers.length - 1))] : null;

  const goPrev = () => {
    if (!hasCovers) return;
    setIndex((i) => (i - 1 + covers.length) % covers.length);
  };
  const goNext = () => {
    if (!hasCovers) return;
    setIndex((i) => (i + 1) % covers.length);
  };

  const openLightbox = () => {
    if (!hasCovers) return;
    setLightboxIndex(index);
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCovers, covers.length]);

  useEffect(() => {
    if (!thumbsRef.current) return;
    const el = thumbsRef.current.querySelector(`[data-thumb-idx="${index}"]`);
    if (!el) return;
    el.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
  }, [index]);

  if (!hasCovers) {
    return (
      <div className="border border-white/10 bg-white/5 p-8 text-center">
        <p className="text-gray-300">
          Add full-cover images to <span className="text-[#ffab3e]">/public/assets/Book cover design/</span>{" "}
          and refresh.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Main cover area */}
      <div className="relative bg-black border border-black">
        <button
          type="button"
          aria-label="Previous cover"
          onClick={goPrev}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-16 h-16 md:w-20 md:h-20 bg-black/60 text-gray-300 text-5xl md:text-6xl leading-none hover:bg-black/75 hover:text-gray-100 focus:outline-none focus-visible:ring-0"
          style={{ outline: "none" }}
        >
          ‹
        </button>

        <button
          type="button"
          aria-label="Next cover"
          onClick={goNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-16 h-16 md:w-20 md:h-20 bg-black/60 text-gray-300 text-5xl md:text-6xl leading-none hover:bg-black/75 hover:text-gray-100 focus:outline-none focus-visible:ring-0"
          style={{ outline: "none" }}
        >
          ›
        </button>

        <div className="flex items-center justify-center px-6 py-6 md:py-8">
          <button
            type="button"
            onClick={openLightbox}
            className="cursor-zoom-in bg-black"
            aria-label="Open full size cover"
            style={{ outline: "none" }}
          >
            <img
              src={active.src}
              alt={active.alt ?? filenameToLabel(active.name)}
              className="max-h-[72vh] w-auto max-w-full object-contain block bg-black"
              loading="eager"
            />
          </button>
        </div>
      </div>

      {/* Thumbnails */}
      <div className="mt-4 bg-black border border-black px-3 py-3">
        <div
          ref={thumbsRef}
          className="hover-scrollbar flex gap-3 overflow-x-auto overscroll-x-contain"
        >
          {covers.map((c, i) => {
            const isActive = i === index;
            return (
              <button
                key={c.src}
                type="button"
                data-thumb-idx={i}
                onClick={() => setIndex(i)}
                className={`shrink-0 border ${isActive ? "border-[#ffab3e]" : "border-white/15"} bg-black`}
                aria-label={`View ${filenameToLabel(c.name)}`}
                style={{ outline: "none" }}
              >
                <img
                  src={c.src}
                  alt={c.alt ?? filenameToLabel(c.name)}
                  className={`h-24 md:h-28 w-auto object-contain block bg-black ${
                    isActive ? "opacity-100" : "opacity-70 hover:opacity-100"
                  }`}
                  loading="lazy"
                />
              </button>
            );
          })}
        </div>
      </div>

      <LightboxModal
        items={covers}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onPrev={() =>
          setLightboxIndex((prev) => {
            if (prev == null) return prev;
            const next = (prev - 1 + covers.length) % covers.length;
            setIndex(next);
            return next;
          })
        }
        onNext={() =>
          setLightboxIndex((prev) => {
            if (prev == null) return prev;
            const next = (prev + 1) % covers.length;
            setIndex(next);
            return next;
          })
        }
      />
    </div>
  );
}

